import { Temporal } from '@js-temporal/polyfill';
import { DateRange } from './types';

export const getDefaultStart = (
  defaultRange: DateRange | undefined,
  valueRange: DateRange | undefined
): Date | null => {
  if (defaultRange?.startDate) {
    return new Date(defaultRange.startDate);
  }
  if (valueRange?.startDate) {
    return new Date(valueRange.startDate);
  }
  return null;
};

export const getDefaultEnd = (defaultRange: DateRange | undefined, valueRange: DateRange | undefined): Date | null => {
  if (defaultRange?.endDate) {
    return new Date(defaultRange.endDate);
  }
  if (valueRange?.endDate) {
    return new Date(valueRange.endDate);
  }
  return null;
};

export const getSystemTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

const timezoneOffsetCache = new Map<string, number | null>();
// UTC offset of an IANA timezone in minutes, DST-aware for the given instant (defaults to now),
// or null if the timezone can't be resolved. Pass the target instant when the offset matters at a
// specific date/time rather than today, since offsets shift across DST transitions.
export const getTimezoneOffsetMinutes = (timeZone: string, atInstant?: Date | number): number | null => {
  // only cache lookups for the current instant; explicit instants are unbounded and not cached
  const useCache = atInstant === undefined;
  if (useCache) {
    const cached = timezoneOffsetCache.get(timeZone);
    if (cached !== undefined) return cached;
  }

  let result: number | null;
  try {
    const epochMilliseconds =
      atInstant === undefined ? Date.now() : typeof atInstant === 'number' ? atInstant : atInstant.getTime();
    const offsetNanoseconds =
      Temporal.Instant.fromEpochMilliseconds(epochMilliseconds).toZonedDateTimeISO(timeZone).offsetNanoseconds;
    result = offsetNanoseconds / 60_000_000_000;
  } catch {
    result = null;
  }

  if (useCache) timezoneOffsetCache.set(timeZone, result);
  return result;
};

export const getUserTimezone = () => {
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  const stored = localStorage.getItem('last-timezone');
  if (!stored) return userTz;
  try {
    const parsed = JSON.parse(stored);
    return parsed?.item || userTz;
  } catch {
    return stored;
  }
};

export const getAdjustedDate = (timezone: string, onComplete: (date: Date | null) => void) => (date: Date | null) => {
  if (date) {
    const zonedDateTime = Temporal.ZonedDateTime.from({
      timeZone: timezone,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: 0,
    });

    onComplete(new Date(zonedDateTime.toString().split('[')[0]));
  } else {
    onComplete(null);
  }
};
